import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, bikeIsRideable } from "../../config";
import { HeartHandshake, Loader2, Store, X } from "lucide-react";

// ---- Reserva manual de tienda / teléfono (también desde la ficha de cliente) ----
export default function ManualBookingForm({ bikes, initial, headers, onClose, onSave }) {
  const [form, setForm] = useState({
    bike_slug: "", start_date: "", end_date: "", name: "", phone: "", email: "", total: "", notes: "",
    deposit_amount: "", cancellation_fee: "50", pickup_time: "10:00", return_time: "19:00", unit_index: "",
    ...(initial || {}),
  });
  const [busy, setBusy] = useState(false);
  const [loyal, setLoyal] = useState(null);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const available = bikes.filter((b) => b.active !== false && bikeIsRideable(b) && !b.coming_soon);
  const selectedBike = bikes.find((b) => b.slug === form.bike_slug);
  const stock = selectedBike?.stock || 1;
  const units = selectedBike?.units || [];
  const selectBike = (slug) => {
    const bike = bikes.find((b) => b.slug === slug);
    setForm({
      ...form,
      bike_slug: slug,
      unit_index: "",
      deposit_amount: form.deposit_amount === "" && bike ? String(bike.deposit ?? "") : form.deposit_amount,
    });
  };

  // Aviso de cliente repetidor: al escribir el email, miramos si ya alquiló antes
  useEffect(() => {
    const email = (form.email || "").toLowerCase().trim();
    if (!email.includes("@") || !headers) { setLoyal(null); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/admin/customers/lookup`, { headers, params: { email } })
        .then(({ data }) => setLoyal(data.found ? data : null))
        .catch(() => setLoyal(null));
    }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.email]);

  const save = async () => {
    setBusy(true);
    try {
      await onSave({
        ...form,
        total: parseFloat(form.total) || 0,
        deposit_amount: form.deposit_amount === "" ? null : parseFloat(form.deposit_amount) || 0,
        cancellation_fee: form.cancellation_fee === "" ? null : parseFloat(form.cancellation_fee) || 0,
        unit_index: form.unit_index === "" ? null : Number(form.unit_index),
      });
      onClose();
    } catch (err) {
      const code = err.response?.data?.detail;
      toast.error(code === "dates_unavailable" ? "Esas fechas ya están ocupadas para esa moto"
        : code === "unit_taken" ? "Esa matrícula ya está ocupada en esas fechas"
        : code === "invalid_unit" ? "Unidad no válida para este modelo"
        : code === "bike_unavailable" ? "Esa moto no está disponible (taller o próximamente)"
        : code === "read_only" || code === "forbidden_role" ? "Tu rol no permite registrar reservas"
        : "Error al registrar la reserva");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="manual-booking-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative card-brutal w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" data-lenis-prevent>
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display font-bold text-topacio-ink flex items-center gap-2"><Store className="w-5 h-5 text-topacio-red" /> Reserva de tienda / teléfono</h3>
          <button data-testid="manual-booking-close" onClick={onClose} className="text-topacio-faded hover:text-topacio-ink"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Moto</label>
            <select data-testid="manual-bike-select" value={form.bike_slug} onChange={(e) => selectBike(e.target.value)} className="input-brutal mt-1">
              <option value="">Elige la moto…</option>
              {available.map((b) => <option key={b.slug} value={b.slug}>{b.name} · {b.price_1d}€/día</option>)}
            </select>
          </div>
          {stock > 1 && (
            <div className="col-span-2">
              <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Unidad / matrícula asignada</label>
              <select data-testid="manual-unit-select" value={form.unit_index} onChange={(e) => set("unit_index", e.target.value)} className="input-brutal mt-1">
                <option value="">Sin asignar (cualquiera libre)</option>
                {Array.from({ length: stock }).map((_, i) => (
                  <option key={i} value={i}>{units[i]?.internal_name || units[i]?.plate || `Unidad ${i + 1}`}{units[i]?.plate ? ` · ${units[i].plate}` : ""}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Recogida</label>
            <input data-testid="manual-start-date" type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className="input-brutal mt-1" />
          </div>
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Devolución</label>
            <input data-testid="manual-end-date" type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className="input-brutal mt-1" />
          </div>
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Hora recogida</label>
            <input data-testid="manual-pickup-time" type="time" value={form.pickup_time} onChange={(e) => set("pickup_time", e.target.value)} className="input-brutal mt-1" />
          </div>
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Hora devolución</label>
            <input data-testid="manual-return-time" type="time" value={form.return_time} onChange={(e) => set("return_time", e.target.value)} className="input-brutal mt-1" />
          </div>
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Nombre del cliente</label>
            <input data-testid="manual-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="input-brutal mt-1" />
          </div>
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Teléfono</label>
            <input data-testid="manual-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="input-brutal mt-1" />
          </div>
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Precio pactado (€)</label>
            <input data-testid="manual-total" type="number" min="0" step="0.01" value={form.total} onChange={(e) => set("total", e.target.value)} className="input-brutal mt-1" />
          </div>
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Email (opcional)</label>
            <input data-testid="manual-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="input-brutal mt-1" />
          </div>
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Fianza del alquiler (€)</label>
            <input data-testid="manual-deposit" type="number" min="0" step="0.01" value={form.deposit_amount} onChange={(e) => set("deposit_amount", e.target.value)} className="input-brutal mt-1" />
          </div>
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Penalización cancelación (€)</label>
            <input data-testid="manual-cancellation-fee" type="number" min="0" step="0.01" value={form.cancellation_fee} onChange={(e) => set("cancellation_fee", e.target.value)} className="input-brutal mt-1" />
          </div>
          {loyal && (
            <div data-testid="repeat-customer-hint" className="col-span-2 flex flex-wrap items-center gap-x-3 gap-y-1 bg-brand/40 border-2 border-topacio-ink rounded-lg px-3 py-2">
              <span className="flex items-center gap-1.5 text-xs font-extrabold text-topacio-ink">
                <HeartHandshake className="w-4 h-4 text-topacio-red" />
                Cliente fiel: {loyal.bookings > 0 ? `${loyal.bookings} alquiler${loyal.bookings === 1 ? "" : "es"} · ${loyal.spent}€` : "ficha existente"}{loyal.favorite_bike ? ` · suele llevar ${loyal.favorite_bike}` : ""}
                {loyal.docs_complete && <span data-testid="repeat-docs-ok" className="badge-brutal bg-topacio-green text-white !text-[8px] ml-1">Docs OK</span>}
              </span>
              {(loyal.name || loyal.phone) && (
                <button type="button" data-testid="repeat-autofill-btn"
                  onClick={() => setForm({ ...form, name: form.name || loyal.name || "", phone: form.phone || loyal.phone || "" })}
                  className="text-[10px] font-extrabold uppercase tracking-wider underline underline-offset-2 text-topacio-faded hover:text-topacio-ink transition-colors">
                  Rellenar nombre y teléfono
                </button>
              )}
            </div>
          )}
          <div className="col-span-2">
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Notas</label>
            <input data-testid="manual-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="input-brutal mt-1" placeholder="Viene por el anuncio de Instagram, quiere casco XL…" />
          </div>
        </div>
        <p className="text-[10px] text-topacio-faded font-semibold mt-3">La reserva queda confirmada y bloquea esas fechas en el calendario online. Cuenta como canal «Tienda / publicidad» en el NOC. Si indicas email, el cliente recibe el enlace de precheck-in para enviar DNI y carnet una sola vez.</p>
        <button data-testid="manual-booking-save" disabled={busy || !form.bike_slug || !form.start_date || !form.end_date || !form.name || form.total === ""}
          onClick={save} className="btn-primary w-full mt-4">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          Registrar reserva
        </button>
      </div>
    </div>
  );
}
